import { useState, useEffect } from "react";

import {
    ApplePaySupportedMerchantCapabilities,
    ApplePaySupportedNetworks,
    CountryCodes,
    DigitalWalletProviders,
    PaymentMethodType,
    TransactionStatus,
    Currency } from "../../common/constants";
import ApiClient from "../../ApiClient";

const apiClient = ApiClient();

const usePaymentLink = (profileName, orderRef, merchantId) => {
    const [isLoading, setIsLoading] = useState(true);
    const [isFetchingPaymentDetails, setIsFetchingPaymentDetails] = useState(true);
    const [isFetchingMerchantLogo, setIsFetchingMerchantLogo] = useState(true);
    const [isPaying, setIsPaying] = useState(false);
    const [paymentLinkDetails, setPaymentLinkDetails] = useState(null);
    const [merchantLogo, setMerchantLogo] = useState(null);
    const [error, setError] = useState(null);
    const [showAuthView, setShowAuthView] = useState(false);
    const [authViewHtml, setAuthViewHtml] = useState('');
    const [authViewUrl, setAuthViewUrl] = useState('');

    const { 
        pay,
        applePayTransactionStartSession,
        payV2,
        getLiveLinkDetails,
        getMerchantLogo,
        initLiveLinkPayment,
        updateTransactionMetaData,
        getAuthenticationView,
        getAuthenticationStatus } = apiClient;

    useEffect(() => {
        if (!orderRef) {
            setIsFetchingPaymentDetails(false);
            return;
        }

        const onGetLiveLinkDetailsSuccess = (response) => {
            if (response.succeeded === true) {
                setPaymentLinkDetails(response.response);
            }
            else {
                onFail(response.error_message);
            }
        }

        const onFail = (errorMsg) => {
            setError(errorMsg ?? "Couldn't fetch payment details");
            setIsFetchingPaymentDetails(false);
        }

        const fetchDetails = async () => {
            try {
                setIsFetchingPaymentDetails(true); 
                await getLiveLinkDetails(profileName, orderRef, onGetLiveLinkDetailsSuccess, onFail);
            } catch (err) {
                setError(err);
            } finally {
                setIsFetchingPaymentDetails(false); 
            }
        };

        fetchDetails();
    }, [orderRef, profileName, getLiveLinkDetails]);

    useEffect(() => {
        if (!merchantId) {
            setIsFetchingMerchantLogo(false);
            return;
        }

        const onGetMerchantLogoSuccess = (response) => {
            if (response.succeeded === true) {
                setMerchantLogo(response.response.merchant_logo);
            } else {
                onFail();
            }
        }

        const onFail = () => {
            setIsFetchingMerchantLogo(false)
        }

        const fetchLogo = async () => {
            try {
                setIsFetchingMerchantLogo(true);
                await getMerchantLogo(merchantId, onGetMerchantLogoSuccess, onFail);
            } catch (err) {
                console.log(err);
            } finally {
                setIsFetchingMerchantLogo(false);
            }
        }

        fetchLogo();
    }, [merchantId, getMerchantLogo]);

    useEffect(() => {
        setIsLoading(isFetchingPaymentDetails || isFetchingMerchantLogo);
    }, [isFetchingPaymentDetails, isFetchingMerchantLogo]);

    const getExpirationMonth = (date) => {
        return +date.substring(0, 2);
    }

    const getExpirationYear = (date) => {
        const year = Math.floor(new Date().getFullYear() / 100) * 100;
        return year + (+date.substring(3, 5));
    }

    const initiatePayment = async (selectedPaymentMethod) => {
        if (!profileName || !orderRef) return;

        var authToken = null;
        var sessionId = null;
        var failUrl = '';

        const getTokenFromFullUrl = (url) => {
            const parsedUrl = new URL(url);
            return parsedUrl.searchParams.get("token");
        };

        const onSuccess = (response) => {
            if (response.succeeded === true) {
                authToken = getTokenFromFullUrl(response.response.checkout_url);
                sessionId = response.response.session_id;
                failUrl = response.response.failure_url;
            } else {
                onFail(response);
            }
        }

        const onFail = (response) => {
            console.log("fail: ", response);
            setError(response.error_message);
            setIsPaying(false);
        }

        try {
            setIsPaying(true);
            await initLiveLinkPayment(profileName, {'payment_method_type': selectedPaymentMethod}, orderRef, onSuccess, onFail);
        } catch (e) {
            setError(e);
        } finally {
            setIsPaying(false);
            return { authToken, sessionId, failUrl } ;
        }
    };

    const redirectToSuccess = (responseDto) => {
        window.location.replace(
            `${responseDto.redirection_url}?${new URLSearchParams(responseDto).toString()}`
        );
    };

    const redirectToFailure = (
        failureUrl, errorCode, errorMessage, sessionId) => {
        window.location.replace(
            `${failureUrl}?error_code=${errorCode ?? ''}&error_message=${errorMessage ?? 'Payment failed'}&session_id=${sessionId ?? ''}`
        );
    };

    const handleApplePayPayment = (merchantName, paymentSession) => {
        setIsPaying(true);
        const paymentRequest = {
            countryCode: CountryCodes.EGYPT,
            currencyCode: Currency.EGP,
            supportedNetworks: ApplePaySupportedNetworks,
            merchantCapabilities: ApplePaySupportedMerchantCapabilities,
            total: {
                label: merchantName,
                amount: paymentLinkDetails.amount.toFixed(2)
            },
        };

        const session = new window.ApplePaySession(3, paymentRequest);

        const onMerchantValidationSuccess = (response) => {
            if (Object.hasOwn(response, "succeeded") && response.succeeded && response.response?.session) {
                return session.completeMerchantValidation(response.response?.session);
            }
            alert(response.error_message || "Apple pay merchant validation error")
            session.abort();
        }

        const onMerchantValidationFailure = (error) => {
            alert(error.error_message || "Apple pay merchant validation error")
            session.abort();
        }

        session.onvalidatemerchant = async (event) => {
            const requestDTO = {
                session_url: event.validationURL
            }
            await applePayTransactionStartSession(
                requestDTO, paymentSession.authToken, onMerchantValidationSuccess, onMerchantValidationFailure);
        };
        const onSuccess = (responseDto) => {
            if (responseDto?.succeeded) {
                if (!responseDto.error_message && responseDto.transaction_status === TransactionStatus.Successful) {
                    window.location.replace(`${responseDto.redirection_url}?${new URLSearchParams(responseDto).toString()}`);
                } else {
                    const errorMessage = responseDto.error_message ?? "Payment failed";
                    window.location.replace(`${responseDto.redirection_url}?error_code=${responseDto.error_code}&error_message=${errorMessage}`);
                }
            } else if (responseDto?.succeeded === false) {
                setError(responseDto.error_message);
            } else {
                setError("Something went wrong");
            }
        }

        const onFail = (response) => {
            redirectToFailure(
                response.redirection_url ?? paymentSession.failUrl,
                response.errorCode,
                response.errorMessage ?? "Payment Failed",
                paymentSession.sessionId);
        }

        const onPaymentChargeSuccess = (response) => {
            if (response.succeeded && !response.error_message) {
                session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
                onSuccess(response)
            } else {
                session.completePayment(window.ApplePaySession.STATUS_FAILURE);
                onFail(response);
            }
        }

        const onPaymentChargeFail = (error) => {
            session.completePayment(window.ApplePaySession.STATUS_FAILURE);
            redirectToFailure(paymentSession.failUrl, null, error, paymentSession.sessionId);
        }

        session.onpaymentauthorized = async (event) => {
            try {
                const payload = {
                    session_id: paymentSession.sessionId,
                    payment_token: JSON.stringify(event.payment.token.paymentData),
                    digital_wallet_provider: DigitalWalletProviders.ApplePay
                };
                await payV2(payload, paymentSession.authToken, onPaymentChargeSuccess, onPaymentChargeFail);
            } catch (err) {
                session.completePayment(window.ApplePaySession.STATUS_FAILURE);
                redirectToFailure(paymentSession.failUrl, null, error, paymentSession.sessionId);
            }
        };

        session.oncancel = () => {
            setIsPaying(false);
        };

        session.begin();
    };

    const updateMetadata = async (authToken, metadata) => {
        const onSuccess = (response) => {
            if (response.succeeded !== true) {
                onFail(response.error_message);
            }
            setIsPaying(false);
        };

        const onFail = (errorMsg) => {
            setError(errorMsg ?? "Couldn't process your request please try again later");
            setIsPaying(false);
        };

        try {
            setIsPaying(true);
            await updateTransactionMetaData(authToken, metadata, onSuccess, onFail);
            return true;
        } catch (err) {
            onFail(err);
            return false;
        } finally {
            setIsPaying(false);
        }
    }

    const handlePayment = async (formData, paymentSession, selectedPaymentMethod) => {
        const getPaymentTransactionRequestDto = (formData) => {
            if (selectedPaymentMethod === PaymentMethodType.Wallet) {
                return {
                        "payment_number": formData.payment_number.replace(/ /g, ''),
                        "session_id": paymentSession.sessionId,
                    };
            }

            return {
                "payment_number": formData.payment_number.replace(/ /g, ''),
                "expiration_month": getExpirationMonth(formData.expiry_date),
                "expiration_year": getExpirationYear(formData.expiry_date),
                "first_name": formData.first_name,
                "last_name": formData.last_name,
                "security_code": formData.cvv,
                "should_save_payment_method": formData.should_save_payment_method,
                "session_id": paymentSession.sessionId,
            }
        }

        const onSuccess = (response) => {
            if (response?.succeeded) {
                if (!response.error_message && response.transaction_status === TransactionStatus.Successful) {
                    redirectToSuccess(response);
                    return
                }
            }
            onFail(response);
        };

        const onFail = (response) => {
            redirectToFailure(
                response?.redirection_url, response?.error_code, response?.error_message ?? "Payment failed", paymentSession.sessionId);
        };

        try {
            setIsLoading(true);
            await pay(getPaymentTransactionRequestDto(formData), paymentSession.authToken, onSuccess, onFail);
        } catch (err) {
            setError(err);
            redirectToFailure(
                paymentSession.failUrl, err.error_code, err.error_message ?? "Payment failed", paymentSession.sessionId);
        }
    };

    const handleAuthentication = async (formData, paymentSession, selectedPaymentMethod) => {
        const getAuthenticationViewRequestDto = (formData) => {
            if (selectedPaymentMethod === PaymentMethodType.Wallet) {
                return {
                    "payment_number": formData.payment_number.replace(/ /g, ''),
                    "session_id": paymentSession.sessionId,
                };
            }

            return {
                "payment_number": formData.payment_number.replace(/ /g, ''),
                "expiration_month": getExpirationMonth(formData.expiry_date),
                "expiration_year": getExpirationYear(formData.expiry_date),
                "first_name": formData.first_name,
                "last_name": formData.last_name,
                "session_id": paymentSession.sessionId,
                "cvv": formData.cvv,
            };
        };

        const onSuccess = (responseDto) => {
            if (responseDto?.succeeded) {
                console.log(responseDto);
                setShowAuthView(true);
                setAuthViewHtml(responseDto.otpHtmlContent);
                setAuthViewUrl(responseDto.otpUrl);

                const intervalId = setInterval(async () => {
                    await getAuthenticationStatus(paymentSession.authToken, paymentSession.sessionId,
                        (statusDto) => {
                            if (statusDto?.succeeded && statusDto.status === "InProgress") return;

                            clearInterval(intervalId);
                            setShowAuthView(false);

                            if (statusDto?.succeeded && statusDto.status === "Successful") {
                                handlePayment(formData, paymentSession, selectedPaymentMethod);
                            } else {
                                redirectToFailure(
                                    paymentSession.failUrl, statusDto?.error_code, statusDto?.error_message ?? "DO_NOT_PROCEED", paymentSession.sessionId);
                            }
                        },
                        (error) => {
                            clearInterval(intervalId);
                            setShowAuthView(false);
                            redirectToFailure(paymentSession.failUrl, null, error?.message, paymentSession.sessionId);
                        }
                    );
                }, 5000);

            } else {
                redirectToFailure(paymentSession.failUrl, responseDto?.error_code, responseDto?.error_message, paymentSession.sessionId);
            }
        };

        const onFail = (response) => {
            redirectToFailure(paymentSession.failUrl, response?.error_code, response?.error_message, paymentSession.sessionId);
        };

        try {
            setIsLoading(true);
            await getAuthenticationView(
                getAuthenticationViewRequestDto(formData), paymentSession.authToken, onSuccess, onFail);
        } catch (err) {
            redirectToFailure(paymentSession.failUrl, null, "Something went wrong", paymentSession.sessionId);
        }
    };

    const submitPayment = async (paymentSession, formData, selectedPaymentMethod, merchantName) => {
        if (selectedPaymentMethod === PaymentMethodType.ApplePay) {
            return handleApplePayPayment(merchantName, paymentSession);
        }

        if (paymentLinkDetails.merchant_config.is_3ds_required || selectedPaymentMethod === PaymentMethodType.Wallet) {
            await handleAuthentication(formData, paymentSession, selectedPaymentMethod);
        }
        else {
            await handlePayment(formData, paymentSession, selectedPaymentMethod);
        }
    };

    return {
        isLoading,
        error,
        paymentLinkDetails,
        merchantLogo,
        isPaying,
        initiatePayment,
        submitPayment,
        updateMetadata,
        showAuthView,
        authViewHtml,
        authViewUrl };
};

export default usePaymentLink;
